/**
 * useQuota — Custom hook for auto-grade quota state with caching.
 * LAUNCH:C6.P3
 */
import { useState, useEffect, useCallback } from 'react';
import { checkQuota } from '../services/autoGradeService';

const CACHE_TTL = 60000; // 60 seconds

// Module-level cache shared across hook instances
let quotaCache = { data: null, timestamp: 0 };

export function resetQuotaCache() {
  quotaCache = { data: null, timestamp: 0 };
}

export default function useQuota() {
  const [quota, setQuota] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchQuota = useCallback(async (force = false) => {
    if (!force && quotaCache.data) {
      const elapsed = Date.now() - quotaCache.timestamp;
      if (elapsed < CACHE_TTL) {
        setQuota(quotaCache.data);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    try {
      const data = await checkQuota();
      const normalized = {
        canGrade: data.can_grade,
        tier: data.tier,
        limit: data.limit,
        used: data.used,
        remaining: data.remaining,
        resetsAt: data.resets_at,
      };
      quotaCache = { data: normalized, timestamp: Date.now() };
      setQuota(normalized);
    } catch (err) {
      console.error('Failed to fetch quota:', err);
    }
    setIsLoading(false);
  }, []);

  const refresh = useCallback(() => {
    return fetchQuota(true);
  }, [fetchQuota]);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  return {
    quota,
    isLoading,
    refresh,
  };
}
